import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, KeyRound, Loader2, ShieldCheck, GitMerge, Trash2 } from 'lucide-react';
import { API } from '../App';
import { getDeviceSubscriptionId, setDeviceSubscriptionId } from '../lib/deviceSubscription';

/**
 * Iter 210 — Cross-device account recovery (anonymous track).
 *
 * Flow:
 *   1. User types in their backup email → we send a magic link + 6-digit code.
 *   2. User pastes the code (or follows the magic link, which prefills the
 *      token query param). We hit /me/recovery/preview to detect conflicts.
 *   3a. If the new device is empty → instant claim.
 *   3b. If there's local data → user picks Merge or Replace.
 *
 * Routes:
 *   /restore                    — type-in flow
 *   /restore?token=<magic>      — magic-link landing
 */
const RestoreAccount = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const magicToken = search.get('token') || '';

  const [step, setStep] = useState(magicToken ? 'preview' : 'request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const newSubscriptionId = getDeviceSubscriptionId();

  // If a magic-link token is in the URL, fetch preview immediately.
  useEffect(() => {
    if (!magicToken) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/me/recovery/preview`, {
          token: magicToken, newSubscriptionId,
        });
        if (!alive) return;
        setPreviewData(r.data);
        if (!r.data.conflict) {
          // No conflict → claim straight away. Inlined so we don't have to
          // add the (stateful) `claim` helper as an effect dependency.
          const cr = await axios.post(`${API}/me/recovery/claim`, {
            token: magicToken, newSubscriptionId,
          });
          if (!alive) return;
          setDeviceSubscriptionId(newSubscriptionId);
          const { favourites = 0, follows = 0, areas = 0 } = cr.data.restored || {};
          toast.success(`Account restored — ${follows} follows, ${favourites} favourites, ${areas} towns`);
          setTimeout(() => { if (alive) navigate('/'); }, 800);
        } else {
          setStep('conflict');
        }
      } catch (err) {
        if (!alive) return;
        toast.error(err?.response?.data?.detail || 'This recovery link is no longer valid');
        setStep('request');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [magicToken, newSubscriptionId, navigate]);

  const requestEmail = async () => {
    const value = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      toast.error('Please enter the email you used as backup');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/me/recovery/request`, { email: value });
      toast.success('If that email is registered, a magic link and code are on the way');
      setStep('code');
    } catch (err) {
      toast.error('Could not send recovery email');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error('The code is 6 digits');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/me/recovery/preview`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        newSubscriptionId,
      });
      setPreviewData(r.data);
      if (!r.data.conflict) {
        await claim({ email: email.trim().toLowerCase(), code: code.trim() });
      } else {
        setStep('conflict');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const claim = async ({ token, email: e, code: c, action } = {}) => {
    setLoading(true);
    try {
      const body = { newSubscriptionId, ...(action ? { action } : {}) };
      // Resolve the credentials from caller args, or fall back to current
      // page state (used by the conflict-resolution buttons).
      const effectiveToken = token ?? magicToken;
      const effectiveEmail = e ?? email.trim().toLowerCase();
      const effectiveCode = c ?? code.trim();
      if (effectiveToken) {
        body.token = effectiveToken;
      } else {
        body.email = effectiveEmail;
        body.code = effectiveCode;
      }
      const r = await axios.post(`${API}/me/recovery/claim`, body);
      if (r.data.status === 'conflict') {
        setPreviewData({
          conflict: true,
          backup: r.data.backup,
          incoming: r.data.incoming,
        });
        setStep('conflict');
        return;
      }
      setDeviceSubscriptionId(newSubscriptionId);
      const { favourites = 0, follows = 0, areas = 0 } = r.data.restored || {};
      toast.success(`Account restored — ${follows} follows, ${favourites} favourites, ${areas} towns`);
      setTimeout(() => navigate('/'), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not restore your account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="restore-account-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 rounded hover:bg-slate-100" data-testid="restore-back">
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </button>
          <h1 className="text-base font-bold text-slate-900">Restore my account</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          <div className="text-center">
            <div className="inline-flex p-3 bg-violet-50 rounded-full mb-3">
              <ShieldCheck className="h-7 w-7 text-violet-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Get your follows back</h2>
            <p className="text-[13px] text-slate-500 mt-1">
              Restore your followed businesses, favourites, towns and notification preferences
              from your backup email.
            </p>
          </div>

          {step === 'request' && (
            <div className="space-y-3" data-testid="restore-request-step">
              <label className="block">
                <span className="text-[12px] font-semibold text-slate-700">Backup email</span>
                <div className="mt-1 flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <input
                    type="email" autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 outline-none text-sm"
                    data-testid="restore-email-input"
                  />
                </div>
              </label>
              <button
                onClick={requestEmail} disabled={loading}
                className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="restore-send-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send magic link & code
              </button>
              <p className="text-[11px] text-slate-400 text-center">
                We&apos;ll never reveal whether an email is registered. You&apos;ll get the email only if it matches a verified backup.
              </p>
            </div>
          )}

          {step === 'code' && (
            <div className="space-y-3" data-testid="restore-code-step">
              <p className="text-[12px] text-slate-600 text-center">
                We sent a magic link and a 6-digit code to <strong>{email}</strong>. Tap the link
                in the email, or paste the code below.
              </p>
              <label className="block">
                <span className="text-[12px] font-semibold text-slate-700">6-digit code</span>
                <div className="mt-1 flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-2">
                  <KeyRound className="h-4 w-4 text-slate-400" />
                  <input
                    autoFocus
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 outline-none text-center text-xl font-bold tracking-widest"
                    data-testid="restore-code-input"
                  />
                </div>
              </label>
              <button
                onClick={submitCode} disabled={loading}
                className="w-full px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="restore-submit-code-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Continue
              </button>
              <button onClick={() => setStep('request')} className="w-full text-[12px] text-slate-500 hover:underline" data-testid="restore-back-to-email">
                Wrong email? Start over
              </button>
            </div>
          )}

          {step === 'conflict' && previewData?.conflict && (
            <div className="space-y-3" data-testid="restore-conflict-step">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-900">
                <strong>This device already has some data.</strong> Pick how to combine it with
                your backup.
              </div>
              <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-600">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-bold text-slate-900 mb-1">On this device</p>
                  <p>Favourites: <strong>{previewData.incoming?.favourites ?? 0}</strong></p>
                  <p>Follows: <strong>{previewData.incoming?.follows ?? 0}</strong></p>
                  <p>Towns: <strong>{previewData.incoming?.areas ?? 0}</strong></p>
                </div>
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                  <p className="font-bold text-violet-900 mb-1">In your backup</p>
                  <p>Favourites: <strong>{previewData.backup?.favourites ?? 0}</strong></p>
                  <p>Follows: <strong>{previewData.backup?.follows ?? 0}</strong></p>
                  <p>Towns: <strong>{previewData.backup?.areas ?? 0}</strong></p>
                </div>
              </div>
              <button
                onClick={() => claim({ action: 'merge' })} disabled={loading}
                className="w-full px-4 py-3 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="restore-merge-btn"
              >
                <GitMerge className="h-4 w-4" /> Merge — keep everything
              </button>
              <button
                onClick={() => claim({ action: 'replace' })} disabled={loading}
                className="w-full px-4 py-3 bg-white border border-red-200 hover:bg-red-50 disabled:opacity-60 text-red-700 text-sm font-semibold rounded-lg flex items-center justify-center gap-2"
                data-testid="restore-replace-btn"
              >
                <Trash2 className="h-4 w-4" /> Replace — discard local data
              </button>
            </div>
          )}

          {step === 'preview' && loading && (
            <div className="flex items-center justify-center py-6 text-slate-500" data-testid="restore-magic-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Restoring from magic link…
            </div>
          )}
        </div>

        <p className="text-center text-[12px] text-slate-400 mt-6">
          Haven&apos;t set up a backup email yet?{' '}
          <Link to="/preferences" className="text-violet-700 hover:underline">Add one now</Link>
        </p>
      </main>
    </div>
  );
};

export default RestoreAccount;
